import { apiClient } from './client';
import type { Diagnosis } from './diagnosis';

export interface DimensionDetail {
  dimension: string;
  name: string;
  score: number | null;
  answeredCount: number;
  naCount: number;
}

export interface SuggestionOutput {
  questionCode: string;
  indicator: string;
  trigger: 'le2' | 'eq3';
  priority: 'high' | 'mid' | 'adv';
  content: string;
  weight: number;
  dimension: string;
  dimensionName: string;
}

export interface ModuleIssue {
  questionCode: string;
  indicator: string;
  score: number;
  questionNumber?: number;
}

export interface ModuleSummary {
  dimension: string;
  name: string;
  score: number | null;
  issues: ModuleIssue[];
  suggestions: SuggestionOutput[];
}

export interface AiPriority {
  module: string;
  score: number;
  issue: string;
  suggestion: string;
  benefit?: string;
  timeline?: string;
}

export interface AiSuggestionResult {
  overview: string;
  priorities: AiPriority[];
  top3: string[];
}

export interface ScoreResult {
  dimensionScores: Record<string, number>;
  dimensionDetails: DimensionDetail[];
  totalScore: number;
  grade: string;
  gradeLabel: string;
  attentionFlag: boolean;
  suggestions: SuggestionOutput[];
  moduleSummaries: ModuleSummary[];
  excludedNa: string[];
  activeDimensionWeights: Record<string, number>;
}

export interface ReportData {
  diagnosis: Diagnosis;
  score: ScoreResult;
  aiSuggestions?: AiSuggestionResult | null;
}

export async function getReport(id: string): Promise<ReportData> {
  const res = await apiClient.get<ReportData>(`/diagnosis/${id}/report`, { timeout: 60000 });
  return res.data;
}

export async function createLead(data: {
  name: string;
  phone: string;
  companyName?: string;
  diagnosisId?: string;
  message?: string;
}): Promise<{ id: string }> {
  const res = await apiClient.post<{ id: string }>('/leads', data);
  return res.data;
}

/** 生成分享链接（7 天有效） */
export async function createShare(id: string): Promise<{ shareToken: string; shareUrl: string }> {
  const res = await apiClient.post<{ shareToken: string; shareUrl: string }>(`/diagnosis/${id}/share`);
  return res.data;
}

/** 公开分享报告（无需登录） */
export async function getSharedReport(token: string): Promise<ReportData> {
  const res = await apiClient.get<ReportData>(`/report/share/${token}`);
  return res.data;
}

/** 下载 PDF（blob） */
export async function downloadPdf(id: string): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/diagnosis/${id}/pdf`, { responseType: 'blob' });
  return res.data;
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const GRADE_COLORS: Record<string, string> = {
  A: '#52c41a',
  B: '#1677ff',
  C: '#faad14',
  D: '#fa8c16',
  E: '#f5222d',
};

export const PRIORITY_LABEL: Record<string, { text: string; color: string }> = {
  high: { text: '高优', color: 'red' },
  mid: { text: '中优', color: 'orange' },
  adv: { text: '进阶', color: 'blue' },
};
